import { useEffect, useState, type JSX } from 'react';
import {
  type AlertModalProps,
  AlertModal,
} from '@doxyme/ui/components/patterns';
import { useRouter } from 'next/router';

export type NavigationConfirmationAlertProps = {
  alertTextProps: {
    title: string;
    description: string;
    primaryButtonText: string;
    secondaryButtonText: string;
  };
  shouldBlockNavigation: boolean;
  onLeave: () => void;
  domain: string;
  statusIcon?: AlertModalProps['statusIcon'];
  allowClientSideNavigation?: boolean;
};

export const NavigationConfirmationAlert = ({
  alertTextProps,
  shouldBlockNavigation,
  onLeave,
  domain,
  statusIcon = 'warning',
  allowClientSideNavigation = false,
}: NavigationConfirmationAlertProps): JSX.Element | null => {
  const router = useRouter();

  const [isOpen, setIsOpen] = useState(false);
  const [nextRoute, setNextRoute] = useState<string>('');

  const handleRouteChangeStart = (url: string): void => {
    if (shouldBlockNavigation) {
      setIsOpen(true);
      setNextRoute(url);
      // To avoid URL change when click on back button
      history.forward();
      // Prevent of route change nextjs
      throw `${domain} - blocked route change due to unsaved changes`;
    }
  };

  const handleWindowClose = (event: BeforeUnloadEvent): void => {
    if (shouldBlockNavigation) {
      event.preventDefault();
    }
  };

  const removeListener = (): void => {
    window.removeEventListener('beforeunload', handleWindowClose);
    if (!allowClientSideNavigation) {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', handleWindowClose);
    if (!allowClientSideNavigation) {
      router.events.on('routeChangeStart', handleRouteChangeStart);
    }

    return () => {
      removeListener();
    };
  }, [shouldBlockNavigation]);

  const stayOnPage = (): void => {
    setIsOpen(false);
    setNextRoute('');
  };

  const leavePage = (): void => {
    setIsOpen(false);

    onLeave();

    if (Boolean(nextRoute)) {
      removeListener();
      void router.push(nextRoute.replace(router.basePath, ''));
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <AlertModal
      isOpen={isOpen}
      statusIcon={statusIcon}
      title={alertTextProps.title}
      description={alertTextProps.description}
      onClose={stayOnPage}
      primaryButton={{
        text: alertTextProps.primaryButtonText,
        onClick: stayOnPage,
      }}
      secondaryButton={{
        text: alertTextProps.secondaryButtonText,
        onClick: leavePage,
      }}
    />
  );
};
